import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, Observable, filter } from "rxjs";
import { DropdownItem } from "../../../interfaces/dropdown-item.interface";
import { EpocService } from "src/app/features/epoc/epoc.service";
@UntilDestroy()
@Component({
  standalone: true,
  selector: "thera-dropdown",
  templateUrl: "dropdown.component.html",
  styleUrls: ["./dropdown.component.scss"],
  imports: [
    CommonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
  ],
})
export class DropdownComponent implements OnInit {
  private _options: DropdownItem[] | any[] = [];
  private _defaultSelection: any;
  readonly dropdownVariant = DropdownVariant;
  selectedOptions: string[] = [];
  @Input() shouldOptionDisable!: boolean;
  @Input() showRequired = true;
  @Input() isSelectionDisabled: boolean = false;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() set defaultSelection(value: any) {
    this._defaultSelection = value;
    if (!this.isDisabled) this.setDefault(this.options);
  }
  get defaultSelection(): any {
    return this._defaultSelection;
  }
  @Input() set options(options: DropdownItem[] | any[]) {
    this._options = options;
    this.model = null;
    if (this.shouldOptionDisable)
      this._options = this._options.map((item) => {
        return {
          ...item,
          disabled: item.disabled || item.isBlankRequired,
        };
      });
    this.loadData();
  }
  get options(): DropdownItem[] | any[] {
    return this._options;
  }
  @Input() datasource?: Observable<DropdownItem[] | any[]>;
  @Input() label!: string;
  @Input() placeholder: string = "Select";
  @Input() required = false;
  @Input() reload$!: Observable<void>;
  @Input() reloadObsArray$!: Observable<void>[];
  @Input() isDisabled = false;
  @Input() multiple = false;
  @Input() hint?: string;
  @Input() autoSelectByDefault: boolean = true;
  @Input() variant: DropdownVariant = DropdownVariant.BASIC;
  @Input() supressEvents: boolean = false;
  @Input() displayAllOptionForMultiSelect: boolean = false;
  @Output() optionSelected: EventEmitter<unknown> = new EventEmitter();
  @Output() dropdownClosed: EventEmitter<any> = new EventEmitter();
  @Output() focus: EventEmitter<void> = new EventEmitter<void>();
  options$!: Observable<any[]>;
  control = new FormControl();
  model: any;
  selectedValuesDisplay!: string;

  readonly NotApplicableOptionId: number = 99999;

  constructor(
    private cdr: ChangeDetectorRef,
    private epocService: EpocService
  ) {}

  ngOnInit(): void {
    this.formGroup?.registerControl(this.controlName, this.control);

    this.setSelectedOptions();

    this.formGroup
      ?.get(this.controlName)
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter(() => this.multiple)
      )
      .subscribe((value) => {
        this.setSelectedOptions();
      });
    if (this.reload$) {
      this.reload$.pipe(untilDestroyed(this)).subscribe(() => {
        this.loadData();
      });
    }
    if (this.reloadObsArray$) {
      this.reloadObsArray$.forEach((ele) => {
        ele?.pipe(untilDestroyed(this)).subscribe(() => {
          this.loadData();
        });
      });
    }
    this.loadData();

    if (this.isDisabled) {
      // trigger events defaultly
      this.formGroup
        ?.get(this.controlName)
        ?.disable({ onlySelf: false, emitEvent: !this.supressEvents });
    } else {
      this.formGroup
        ?.get(this.controlName)
        ?.enable({ onlySelf: false, emitEvent: !this.supressEvents });
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["defaultSelection"] &&
      !changes["defaultSelection"].firstChange
    ) {
      this.setDefault(this.options);
    }
  }
  setSelectedOptions() {
    const value = this.formGroup?.get(this.controlName)?.value;
    if (value && Array.prototype.isPrototypeOf(value)) {
      this.selectedOptions = this.options
        .filter((option) => value.find((element: any) => element === option.id))
        .map((option) => option.name);
    }
  }

  private loadData() {
    if (this.datasource) {
      this.datasource.subscribe((response: DropdownItem[]) => {
        if (!this.isDisabled) this.setDefault(response);
        if (this.epocService.questionOptions) {
          if (this.controlName.includes("Subgroup")) {
            const newQuestionOption = {
              question: this.controlName,
              options: response,
            };
            const existingIndex = this.epocService.questionOptions.findIndex(
              (option) => option.question === this.controlName
            );

            if (existingIndex !== -1) {
              this.epocService.questionOptions[existingIndex] =
                newQuestionOption;
            } else {
              this.epocService.questionOptions.push(newQuestionOption);
            }
          } else {
            this.epocService.questionOptions.push({
              question: this.controlName,
              options: response,
            });
          }
        }
        const control = this.formGroup?.get(this.controlName);
        const value = control?.value;

        if (value) {
          const validIds = response.map(r => r.id);
        
          const isValid = Array.isArray(value)
            ? value.some(v => validIds.includes(v))
            : validIds.includes(value);
        
          if (!isValid) {
            control.setValue(null);
            control.markAsTouched();
          }
        }
        this.options$ = new BehaviorSubject(response).asObservable();
      });
    } else {
      if (!this.isDisabled) this.setDefault(this.options);

      this.options$ = new BehaviorSubject(this.options).asObservable();
    }
  }

  onOptionSelected(option: any) {
    if (option?.value == null) {
      this.optionSelected.emit(null);
      return;
    }
    if(Array.isArray(option.value) && option?.value?.includes(this.NotApplicableOptionId)){
      this.model = null;
      option.value = option.value.filter((v: number) => v === this.NotApplicableOptionId);
      this.setSelectedValuesDisplay('')
      this.formGroup.get(this.controlName)?.setValue(null);
      this.optionSelected.emit(null);
      this.model = option.id;
      this.formGroup.get(this.controlName)?.setValue([this.NotApplicableOptionId]);
    }
    this.setSelectedValuesDisplay(option.value);
    this.optionSelected.emit(option);
  }

  setDefault(items: any[]) {
    if (items?.length === 1) {
      if (!this.autoSelectByDefault) {
        return;
      }
      if (this.controlName) {
        this.control.setValue(
          this.multiple ? [items[0].id] : items[0].id ?? items[0]
        );
        const defaultValue = this.multiple
          ? [items[0].id]
          : items[0].id ?? items[0];
        this.formGroup?.get(this.controlName)?.setValue(defaultValue);
        this.setSelectedValuesDisplay(defaultValue);
      } else {
        this.model = this.multiple ? [items[0].id] : items[0].id ?? items[0];
        this.setSelectedValuesDisplay(this.model);
      }
    } else {
      let defaultItem;
      if (Array.isArray(this.defaultSelection)) {
        if (this.controlName) {
          this.control.setValue(this.defaultSelection);
        } else {
          this.model = this.defaultSelection;
        }
        this.setSelectedValuesDisplay(this.defaultSelection);
      } else {
        defaultItem = items?.find((item) =>
          item?.id !== undefined && item?.id !== null
            ? item.id == this.defaultSelection
            : item == this.defaultSelection
        );

        if (defaultItem) {
          if (this.controlName) {
            this.control.setValue(defaultItem.id ?? defaultItem);
          } else {
            this.model = defaultItem.id ?? defaultItem;
          }
          this.setSelectedValuesDisplay(defaultItem);
        } else {
          this.model = null;
          this.selectedValuesDisplay = "";
        }
      }
    }
  }

  isChecked() {
    return (
      this.model && this.options && this.model.length === this.options.length
    );
  }

  toggleSelection(change: MatCheckboxChange) {
    if (change.checked) {
      this.selectedValuesDisplay = "All";
      this.optionSelected.emit({ value: this.options.map((x) => x.id) });
      this.model = this.options?.map((m) => m.id);
    } else {
      this.selectedValuesDisplay = "";
      this.optionSelected.emit([]);
      this.model = [];
    }
  }

  setSelectedValuesDisplay(values: any | any[]) {
    if (
      this.displayAllOptionForMultiSelect &&
      this.model?.length > 0 &&
      this.options?.length > 1 &&
      this.options?.length === this.model?.length
    ) {
      this.selectedValuesDisplay = "All";
    } else {
      if (Array.isArray(values)) {
        const selectedValues: any[] = [];
        values.forEach((v: any) => {
          const data = this.options.find(
            (x) =>
              (x.id ? x.id.toString() : x.toString()) ===
              (v.id ? v.id.toString() : v.toString())
          );
          selectedValues.push(data?.name ?? data?.value ?? data);
        });
        this.selectedValuesDisplay = selectedValues
          .filter((x) => x !== undefined)
          .join(", ");
      } else {
        const value = this.options.find(
          (x) =>
            (x.id ? x.id.toString() : x.toString()) ===
            (values.id ? values.id.toString() : values.toString())
        );
        this.selectedValuesDisplay = value?.name ?? value?.value ?? value;
      }
    }
  }

  getSelectedValuesDisplay(): string {
    return this.selectedValuesDisplay;
  }

  onDropdownClosed(isOpened: boolean) {
    if (!isOpened) {
      const selectedOptionIds = this.model;
      if (this.options?.length === 1) {
        if (this.multiple) {
          this.dropdownClosed.emit([this.options[0].id]);
        } else {
          this.dropdownClosed.emit(this.options[0].id);
        }
      } else {
        this.dropdownClosed.emit(selectedOptionIds);
      }
    }
  }

  onFocus(): void {
    this.focus.emit();
  }
}

export enum DropdownVariant {
  BASIC = "BASIC",
  BLUE_SHADED = "BLUE_SHADED",
}